/**
 * Timezone Manager
 *
 * Handles timezone selection and time conversion functionality
 */

import { EventEmitter } from './EventEmitter';
import { TranslationHelper } from './TranslationHelper';
import moment from 'moment';
import 'moment/dist/locale/fr';
import 'moment-timezone';

interface DropdownOptions {
  placement: string;
  triggerType: string;
  offsetSkidding: number;
  offsetDistance: number;
  delay: number;
  ignoreClickOutsideClass: boolean;
  onHide: () => void;
  onShow: () => void;
  onToggle: () => void;
}

interface DropdownInstanceOptions {
  id: string;
  override: boolean;
}

interface DropdownConstructor {
  new (
    targetEl: HTMLElement,
    triggerEl: HTMLElement,
    options: DropdownOptions,
    instanceOptions: DropdownInstanceOptions
  ): {
    show: () => void;
    hide: () => void;
    destroy?: () => void;
  };
}

declare global {
  interface Window {
    Dropdown?: DropdownConstructor;
  }
}

export interface TimezoneOption {
  value: string;
  label: string;
}

export class TimezoneManager {
  private eventEmitter: EventEmitter;
  private dropdownButton: HTMLButtonElement | null = null;
  private dropdownMenu: HTMLElement | null = null;
  private searchInput: HTMLInputElement | null = null;
  private timezoneList: HTMLElement | null = null;
  private selectedTextSpan: HTMLElement | null = null;
  private hiddenInput: HTMLInputElement | null = null;
  private timeElements: NodeListOf<Element> | null = null;
  private allTimezones: TimezoneOption[] = [];
  private filteredTimezones: TimezoneOption[] = [];
  private selectedTimezone: string = '';
  private dropdownInstance: {
    show: () => void;
    hide: () => void;
    destroy?: () => void;
  } | null = null;

  constructor(eventEmitter: EventEmitter) {
    this.eventEmitter = eventEmitter;

    moment.updateLocale('fr', {
      monthsShort: ['Janv.', 'Févr.', 'Mars', 'Avr.', 'Mai', 'Juin', 'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'],
      weekdaysShort: ['Dim.', 'Lun.', 'Mar.', 'Mer.', 'Jeu.', 'Ven.', 'Sam.'],
    });
    
  }

  /**
   * Initialize timezone functionality
   */
  public init(): void {
    try {
      this.findDomElements();
      this.loadTimezones();
      this.populateTimezones();
      this.initializeDropdown();
      this.bindEvents();
      this.setDefaultTimezone();
      this.findTimeElements();
    } catch (error) {
      console.error('[TimezoneManager] Initialization failed:', error);
    }
  }

  /**
   * Find DOM elements
   */
  private findDomElements(): void {
    this.dropdownButton = document.getElementById(
      'gta_timezone_dropdown_button'
    ) as HTMLButtonElement;
    this.dropdownMenu = document.getElementById('gta_timezone_dropdown') as HTMLElement;
    this.searchInput = document.getElementById('gta_timezone_search') as HTMLInputElement;
    this.timezoneList = document.getElementById('gta_timezone_list') as HTMLElement;
    this.selectedTextSpan = document.getElementById('gta_timezone_selected_text') as HTMLElement;
    this.hiddenInput = document.getElementById('gta_timezone_select') as HTMLInputElement;
  }

  /**
   * Load available timezones from moment.js
   */
  private loadTimezones(): void {
    const zones = moment.tz.names();
    this.allTimezones = zones.map((zone: string) => ({
      value: zone,
      label: zone.replace(/_/g, ' '),
    }));

    this.filteredTimezones = [...this.allTimezones];
  }

  /**
   * Populate timezone dropdown with options
   */
  private populateTimezones(): void {
    if (!this.timezoneList) {
      return;
    }

    // Clear existing options
    this.timezoneList.innerHTML = '';

    // Create options
    this.filteredTimezones.forEach(timezone => {
      const listItem = document.createElement('li');
      listItem.innerHTML = `
        <button type="button" 
                class="block! w-full! px-3! py-2! text-left! text-sm! text-gray-700! hover:bg-gray-100! focus:bg-gray-100! focus:outline-none!"
                data-timezone-value="${timezone.value}">
          ${timezone.label}
        </button>
      `;
      if (this.timezoneList) {
        this.timezoneList.appendChild(listItem);
      }
    });
  }

  /**
   * Initialize dropdown using the provided pattern
   */
  private initializeDropdown(): void {
    if (!this.dropdownMenu || !this.dropdownButton) {
      console.warn('[TimezoneManager] Dropdown elements not found');
      return;
    }

    // Set the dropdown menu element
    const $targetEl = this.dropdownMenu;

    // Set the element that triggers the dropdown menu on click
    const $triggerEl = this.dropdownButton;

    // Options with default values
    const options = {
      placement: 'bottom',
      triggerType: 'click',
      offsetSkidding: 0,
      offsetDistance: 10,
      delay: 300,
      ignoreClickOutsideClass: false,
      onHide: () => {
        // eslint-disable-next-line no-console
        console.log('[TimezoneManager] Dropdown has been hidden');
        // Clear search
        if (this.searchInput) {
          this.searchInput.value = '';
          this.filterTimezones();
        }
      },
      onShow: () => {
        // eslint-disable-next-line no-console
        console.log('[TimezoneManager] Dropdown has been shown');
        // Focus search input
        if (this.searchInput) {
          setTimeout(() => {
            this.searchInput?.focus();
          }, 100);
        }
      },
      onToggle: () => {
        // eslint-disable-next-line no-console
        console.log('[TimezoneManager] Dropdown has been toggled');
        // Additional logic can be added here if needed
      },
    };

    // Instance options object
    const instanceOptions = {
      id: 'gta_timezone_dropdown',
      override: true,
    };

    // Initialize dropdown instance
    // Note: This assumes you have a dropdown library loaded (like Flowbite)
    // If using a different library, adjust this accordingly
    try {
      if (typeof window !== 'undefined' && window.Dropdown) {
        this.dropdownInstance = new window.Dropdown(
          $targetEl,
          $triggerEl,
          options,
          instanceOptions
        );
      } else {
        console.warn(
          '[TimezoneManager] Dropdown library not found, falling back to manual implementation'
        );
        this.setupManualDropdown();
      }
    } catch (error) {
      console.warn(
        '[TimezoneManager] Error initializing dropdown, falling back to manual implementation:',
        error
      );
      this.setupManualDropdown();
    }
  }

  /**
   * Bind event listeners (search and timezone selection only - dropdown is handled by library)
   */
  private bindEvents(): void {
    // Search functionality
    if (this.searchInput) {
      this.searchInput.addEventListener('input', () => {
        this.filterTimezones();
      });
    }

    // Timezone selection
    if (this.timezoneList) {
      this.timezoneList.addEventListener('click', e => {
        const target = e.target as HTMLElement;
        const button = target.closest('[data-timezone-value]') as HTMLElement;

        if (button) {
          e.preventDefault();
          const timezoneValue = button.getAttribute('data-timezone-value');
          if (timezoneValue) {
            this.selectTimezone(timezoneValue);
          }
        }
      });
    }
  }

  /**
   * Setup manual dropdown fallback when library is not available
   */
  private setupManualDropdown(): void {
    if (!this.dropdownButton) {
      return;
    }

    // Dropdown toggle for manual implementation
    this.dropdownButton.addEventListener('click', e => {
      e.preventDefault();
      this.toggleDropdownManual();
    });

    // Close dropdown when clicking outside
    document.addEventListener('click', e => {
      const target = e.target as HTMLElement;
      if (
        !target.closest('#gta_timezone_dropdown_button') &&
        !target.closest('#gta_timezone_dropdown')
      ) {
        this.hideDropdown();
      }
    });
  }

  /**
   * Manual dropdown toggle (fallback)
   */
  private toggleDropdownManual(): void {
    if (!this.dropdownMenu) {
      return;
    }

    const isHidden = this.dropdownMenu.classList.contains('hidden');
    if (isHidden) {
      this.showDropdown();
    } else {
      this.hideDropdown();
    }
  }

  /**
   * Show dropdown (either through library or manual)
   */
  private showDropdown(): void {
    if (this.dropdownInstance) {
      this.dropdownInstance.show();
    } else {
      this.manualShowDropdown();
    }
  }

  /**
   * Hide dropdown (either through library or manual)
   */
  private hideDropdown(): void {
    if (this.dropdownInstance) {
      this.dropdownInstance.hide();
    } else {
      this.manualHideDropdown();
    }
  }

  /**
   * Manual show dropdown implementation
   */
  private manualShowDropdown(): void {
    if (!this.dropdownMenu) {
      return;
    }

    this.dropdownMenu.classList.remove('hidden');
    this.dropdownMenu.removeAttribute('aria-hidden');

    // Focus search input
    if (this.searchInput) {
      setTimeout(() => {
        this.searchInput?.focus();
      }, 100);
    }
  }

  /**
   * Manual hide dropdown implementation
   */
  private manualHideDropdown(): void {
    if (!this.dropdownMenu) {
      return;
    }

    this.dropdownMenu.classList.add('hidden');
    this.dropdownMenu.setAttribute('aria-hidden', 'true');

    // Clear search
    if (this.searchInput) {
      this.searchInput.value = '';
      this.filterTimezones();
    }
  }

  /**
   * Filter timezones based on search input
   */
  private filterTimezones(): void {
    if (!this.searchInput) {
      return;
    }

    const searchTerm = this.searchInput.value.toLowerCase();

    this.filteredTimezones = this.allTimezones.filter(
      timezone =>
        timezone.label.toLowerCase().includes(searchTerm) ||
        timezone.value.toLowerCase().includes(searchTerm)
    );

    this.populateTimezones();
  }

  /**
   * Select a timezone
   */
  private selectTimezone(timezoneValue: string): void {
    this.selectedTimezone = timezoneValue;

    // Update UI
    if (this.selectedTextSpan) {
      const selectedOption = this.allTimezones.find(tz => tz.value === timezoneValue);
      this.selectedTextSpan.textContent = selectedOption?.label || timezoneValue;
    }

    // Update hidden input
    if (this.hiddenInput) {
      this.hiddenInput.value = timezoneValue;
    }

    // Close dropdown
    this.hideDropdown();

    // Convert times
    this.convertTimes();

    // Emit timezone change event
    this.eventEmitter.emit('timezoneChanged', {
      timezone: timezoneValue,
      previousTimezone: this.selectedTimezone,
    });
  }

  /**
   * Set default timezone (user's detected timezone)
   */
  private setDefaultTimezone(): void {
    try {
      const detectedTimezone = moment.tz.guess();
      if (detectedTimezone) {
        this.selectTimezone(detectedTimezone);
      }
    } catch (error) {
      console.warn('[TimezoneManager] Could not detect user timezone:', error);
      // Fallback to UTC
      this.selectTimezone('UTC');
    }
  }

  /**
   * Find time elements to convert
   */
  private findTimeElements(): void {
    this.timeElements = document.querySelectorAll(
      '.time-convert, [data-time-convert], [data-original-time]'
    );
  }

  /**
   * Convert times to selected timezone
   */
  private convertTimes(): void {
    if (!this.selectedTimezone) {
      return;
    }
    if (!this.timeElements) {
      this.findTimeElements();
    }

    this.timeElements?.forEach(element => {
      const htmlElement = element as HTMLElement;
      const originalTime =
        htmlElement.getAttribute('data-original') ||
        htmlElement.getAttribute('data-original-time') ||
        htmlElement.getAttribute('data-time');

      if (originalTime) {
        try {
          // Set moment locale based on current language
          const currentLang = TranslationHelper.getCurrentLang();
          const momentLocale = currentLang === 'fr_FR' ? 'fr' : 'en';
          moment.locale(momentLocale);
          
          const convertedTime = moment.tz(originalTime, 'UTC').tz(this.selectedTimezone);

          const format = momentLocale === 'fr' ? 'ddd MMM DD, YYYY - hh:mm A' : 'ddd. MMM DD, YYYY - hh:mm A';
          const formattedTime = convertedTime.format(format);

          // Update the element's text content
          if (htmlElement.tagName === 'INPUT') {
            (htmlElement as HTMLInputElement).value = formattedTime;
          } else {
            htmlElement.textContent = formattedTime;
          }
        } catch (error) {
          console.warn('[TimezoneManager] Error converting time:', originalTime, error);
        }
      }
    });
  }

  /**
   * Refresh time conversions (useful when new content is loaded)
   */
  public refresh(): void {
    this.findTimeElements();
    this.convertTimes();
  }

  /**
   * Get current selected timezone
   */
  public getSelectedTimezone(): string {
    return this.selectedTimezone;
  }

  /**
   * Set timezone programmatically
   */
  public setTimezone(timezone: string): void {
    if (this.allTimezones.some(tz => tz.value === timezone)) {
      this.selectTimezone(timezone);
    }
  }

  /**
   * Get all available timezones
   */
  public getAvailableTimezones(): TimezoneOption[] {
    return [...this.allTimezones];
  }

  /**
   * Destroy and cleanup
   */
  public destroy(): void {
    // Cleanup dropdown instance
    if (this.dropdownInstance) {
      try {
        if (typeof this.dropdownInstance.destroy === 'function') {
          this.dropdownInstance.destroy();
        }
      } catch (error) {
        console.warn('[TimezoneManager] Error destroying dropdown instance:', error);
      }
      this.dropdownInstance = null;
    }

    // Remove event listeners would go here if we stored references
    this.dropdownButton = null;
    this.dropdownMenu = null;
    this.searchInput = null;
    this.timezoneList = null;
    this.selectedTextSpan = null;
    this.hiddenInput = null;
    this.timeElements = null;
  }
}
